// /src/pages/private/admin/PrivateDocuments/donatorPerks/donatorPerksUtils.js

export const PERK_TIERS = [
  { value: 'donor', label: 'Supporter library' },
  { value: 'subscriber', label: 'Patron library' },
  { value: 'premium', label: 'Benefactor library' },
];

export const SUPPORT_LEVEL_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'supporter', label: 'Supporter' },
  { value: 'patron', label: 'Patron' },
  { value: 'benefactor', label: 'Benefactor' },
];

export const PERK_ICON_OPTIONS = [
  { value: 'blue-gem', label: 'Blue gem' },
  { value: 'purple-gem', label: 'Purple gem' },
  { value: 'gold-gem', label: 'Gold gem' },
  { value: 'star', label: 'Star' },
  { value: 'shield', label: 'Shield' },
  { value: 'gift', label: 'Gift' },
];

export const EMPTY_PERK_ROW = {
  title: '',
  description: '',
  tier: 'donor',
  minLifetimeSupportLevel: '',
  minActiveSupportLevel: '',
  iconKey: 'blue-gem',
  requirementLabel: '',
};

export function createPerkClientKey(seed = null) {
  if (seed != null && seed !== '') return `perk-${seed}`;
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `perk-tmp-${crypto.randomUUID()}`;
  }
  return `perk-tmp-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function ensurePerkClientKey(row) {
  if (!row || typeof row !== 'object') {
    return { ...EMPTY_PERK_ROW, clientKey: createPerkClientKey() };
  }
  if (row.clientKey) return row;
  if (row.id != null) return { ...row, clientKey: createPerkClientKey(row.id) };
  return { ...row, clientKey: createPerkClientKey() };
}

export function ensurePerkClientKeys(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.map((row) => ensurePerkClientKey(row));
}

export function isPerkRowEmpty(row) {
  const title = String(row?.title ?? '').trim();
  const description = String(row?.description ?? '').trim();
  return !title && !description;
}

export function normalizePerksForSave(rows) {
  if (!Array.isArray(rows)) return [];
  return rows
    .map((row, index) => ({
      sortOrder: index,
      title: String(row?.title ?? '').trim(),
      description: String(row?.description ?? '').trim(),
      tier: String(row?.tier ?? 'donor').trim().toLowerCase(),
      minLifetimeSupportLevel: String(row?.minLifetimeSupportLevel ?? '').trim().toLowerCase(),
      minActiveSupportLevel: String(row?.minActiveSupportLevel ?? '').trim().toLowerCase(),
      iconKey: String(row?.iconKey ?? 'blue-gem').trim().toLowerCase() || 'blue-gem',
      requirementLabel: String(row?.requirementLabel ?? '').trim(),
      clientKey: row?.clientKey || (row?.id != null ? createPerkClientKey(row.id) : createPerkClientKey()),
      id: row?.id,
    }))
    .filter((row) => !isPerkRowEmpty(row) && ['donor', 'subscriber', 'premium'].includes(row.tier));
}

export function withTrailingEmptyPerkRow(rows) {
  const list = ensurePerkClientKeys(
    Array.isArray(rows) ? rows.map((row) => ({ ...EMPTY_PERK_ROW, ...row })) : [],
  );
  if (list.length === 0 || !isPerkRowEmpty(list[list.length - 1])) {
    list.push({ ...EMPTY_PERK_ROW, clientKey: createPerkClientKey() });
  }
  return list;
}

export function splitIntroParagraphs(introText) {
  return String(introText || '')
    .split(/\n\s*\n/)
    .map((part) => part.trim())
    .filter(Boolean);
}

export function groupPerksByTier(perks) {
  const groups = {
    donor: [],
    subscriber: [],
    premium: [],
  };
  const sorted = [...(Array.isArray(perks) ? perks : [])].sort(
    (a, b) => (Number(a?.sortOrder) || 0) - (Number(b?.sortOrder) || 0),
  );
  sorted.forEach((perk) => {
    if (groups[perk.tier]) groups[perk.tier].push(perk);
  });
  return groups;
}

export function resolvePerkDisplayLevel(perk) {
  return perk?.minActiveSupportLevel
    || perk?.minLifetimeSupportLevel
    || (perk?.tier === 'premium'
      ? 'benefactor'
      : perk?.tier === 'subscriber'
        ? 'patron'
        : 'supporter');
}

export function supportLevelBadgeLabel(level) {
  if (level === 'patron') return 'Patron+';
  if (level === 'benefactor') return 'Benefactor+';
  if (level === 'supporter') return 'Supporter+';
  return 'Member';
}

export function sortSupportLevel(level) {
  if (level === 'benefactor') return 3;
  if (level === 'patron') return 2;
  if (level === 'supporter') return 1;
  return 0;
}

export function filterNextRewardPerks(perks, support) {
  const list = Array.isArray(perks) ? perks : [];
  const currentActive = sortSupportLevel(support?.currentSupportLevel);
  const currentLifetime = sortSupportLevel(support?.highestSupportLevelEverReached);
  return list
    .filter((perk) => {
      const needActive = sortSupportLevel(perk.minActiveSupportLevel);
      const needLifetime = sortSupportLevel(perk.minLifetimeSupportLevel);
      if (needActive <= 0 && needLifetime <= 0) return true;
      return needActive > currentActive || needLifetime > currentLifetime;
    })
    .slice(0, 8);
}
